import { IconWalls } from "$root/components/IconWall";
import { useDecimalPlaces } from "$root/functional-areas/user/useDecimalPlaces";
import { Card } from "$root/widgets-architecture/layout/Card";
import { DebouncedSearchInput } from "@mdotm/shadcn/legacy/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import type { ColDef } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import { type Ref, useEffect, useMemo, useState } from "react";
import { EditorBanners, type EditorBannersHandle } from "./banners/EditorBanners";
import type { Instrument } from "./const";
import { CompositionQuickActions } from "./header/CompositionQuickActions";
import { holdsAssets, keepCashRow } from "./quantity/pinnedCashRow";
import { InstrumentEditorTable } from "./table";

import type { InstrumentCompositionEditorProps, InstrumentCompositionMode } from "./instrumentEditor.types";

const placeholderSearch = "Filter by instrument name, ISIN, asset class or micro asset class";

function InstrumentCompositionEditor({
	editorBannersRef,
	...props
}: InstrumentCompositionEditorProps &
	InstrumentCompositionMode & {
		columns: ColDef<Instrument>[];
		/** Lets the page's save buttons show the banner that blocks the save (`EditorBannersHandle.revealBlocking`). */
		editorBannersRef?: Ref<EditorBannersHandle>;
	}): React.JSX.Element {
	const { instrumentBuilder, entity } = props;

	const [agGridEl, setAgGridEl] = useState<AgGridReact<Instrument> | null>(null);

	const composition = useMemo(() => Array.from(instrumentBuilder.getComposition().values()), [instrumentBuilder]);

	const [searchInstrument, setSearchInstrument] = useState<string>("");
	const { filtered } = useSearchable({
		query: searchInstrument,
		collection: composition,
		extractFn: (instrument) =>
			`${instrument.name}${instrument.identifiers?.ALIAS}${instrument.identifiers?.ISIN}${instrument.identifiers?.CUSIP}${instrument.identifiers?.BLOOMBERG_TICKER}`.toLowerCase(),
	});

	// Quantity mode pins the cash row to the grid bottom; its pinned copy mirrors the SOURCE
	// row node, so cash must survive search filtering (see `keepCashRow`).
	const rows = props.compositionMode === "quantity" ? keepCashRow(filtered, composition) : filtered;
	const hasRows = props.compositionMode === "quantity" ? holdsAssets(composition) : composition.length > 0;

	// Held here, not in the grid, because the normalise banner above the grid acts on it. Emptied with
	// the composition: the grid that held the selection unmounts then, and a new one starts with none.
	const [selection, setSelection] = useState<Instrument[]>([]);
	if (!hasRows && selection.length > 0) {
		setSelection([]);
	}

	const { userDecimalPlacesPreferences } = useDecimalPlaces();

	// biome-ignore lint/correctness/useExhaustiveDependencies: userDecimalPlacesPreferences is a trigger-only dependency — AG Grid must refresh after the state commit
	useEffect(() => {
		const cols =
			props.entity === "INVESTMENT_ENHANCEMENT"
				? ["WEIGHT", "PREVIOUS_WEIGHT", "ENHANCED_WEIGHT", "DELTA_WEIGHT"]
				: ["WEIGHT"];
		agGridEl?.api?.refreshCells({ force: true, columns: cols });
	}, [userDecimalPlacesPreferences, agGridEl]);

	return (
		<Card className="min-w-0 flex-1">
			<div className="flex justify-between">
				<DebouncedSearchInput
					externalQuery={searchInstrument}
					onChange={setSearchInstrument}
					placeholder={placeholderSearch}
					className="max-w-[500px] flex-1"
					data-qualifier="CompositionEditor/Search"
				/>

				<CompositionQuickActions {...props} agGrid={agGridEl} />
			</div>
			<EditorBanners
				{...props}
				handleRef={editorBannersRef}
				hasRows={hasRows}
				rows={rows}
				selection={selection}
				agGrid={agGridEl}
			/>
			{hasRows ? (
				<InstrumentEditorTable
					{...props}
					rows={rows}
					agGridRef={setAgGridEl}
					columns={props.columns}
					selection={selection}
					onChangeSelection={setSelection}
				/>
			) : (
				<IconWalls.EditorEmptyData entity={entity} />
			)}
		</Card>
	);
}

export default InstrumentCompositionEditor;
